import React from 'react';
import Container from 'react-bootstrap/Container';
import Button from "react-bootstrap/Button";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import {DialogService} from '../../../common/Dialog';
import {WizardStepProps} from "../../wizard/Wizard";
import SchemaPlayground from "../playground/SchemaPlayground";
import {UploadSchemaContext} from "./context/UploadSchemaContext";
import {SchemaExistsConfirmation} from "../common/SchemaExistsConfirmation";
import {UnsavedChangesConfirmation} from "../../../common/NavigationConfirmation";
import Loader from "../../loader/Loader";

type SaveSchemaStepState = {
  hasChanges: boolean
}

/**
 * Step implementation dealing with saving the uploaded SOML schema file.
 */
class SaveSchemaStep extends React.Component<WizardStepProps, SaveSchemaStepState> {

  static contextType = UploadSchemaContext;

  constructor(props: WizardStepProps) {
    super(props);
    this.state = {
      hasChanges: false
    };
  }

  componentDidUpdate() {
    // Proceed only if schema is saved successfully.
    if (this.context.saved) {
      // If schema is already bound, then skip the next step.
      if (this.context.isBound) {
        this.props.skip();
      } else {
        this.props.next(true);
      }
    }
  }

  stepBack = () => {
    this.context.removeError();
    return this.props.prev();
  };

  onStepBack = () => {
    if (this.state.hasChanges) {
      DialogService.confirm({
        message: 'The schema has changes which will be lost! Are you sure you want to go back?',
        onConfirm: this.onStepBackConfirm
      });
    } else {
      this.stepBack();
    }
  };

  /**
   * Saves or updates the schema and proceed with the next step if operation is successful.
   */
  onSave = () => {
    this.context.saveSchema(this.context.schema);
  };

  /**
   * Handles changes in the soml schema string which might happen in the schema editor component.
   * Schedules a schema context property change operation through the context.
   * @param {string} schema The updated schema string.
   * @param {boolean} dirty a flag from the editor telling if the schema is changed or not
   */
  onSchemaChange = (schema: string, dirty: boolean) => {
    this.setState({
      hasChanges: dirty
    });
    this.context.changeSchema(schema);
  };

  /**
   * Performs schema update and clears the error.
   */
  confirmOverride = () => {
    // If override is confirmed, we are going to update the schema.
    this.context.updateSchema(this.context.schemaId, this.context.schema);
    this.context.removeError();
  };

  /**
   * Override is denied so just clear the error.
   */
  denyOverride = () => {
    this.context.removeError();
  };

  onStepBackConfirm = () => {
    this.stepBack();
  };

  render() {
    const {isRequestRunning} = this.context;
    return (
      <>
      <Container className="SaveSchemaStep no-padding" fluid>
        <Row className="pb-3">
          <Col>
            <div className="StepActions">
              <Button className="prev-btn"
                      disabled={isRequestRunning}
                      onClick={this.onStepBack}
                      title="Select Schema">
                Select Schema
              </Button>
              <Button className="next-btn"
                      disabled={isRequestRunning}
                      onClick={this.onSave}
                      title="Save">
                Save
              </Button>
              {isRequestRunning ? <Loader /> : null}
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <SchemaPlayground schema={this.context.schema}
                              onChange={this.onSchemaChange}
                              errors={this.context.error}/>
          </Col>
        </Row>
      </Container>
      <UnsavedChangesConfirmation when={this.state.hasChanges || this.context.file}/>
      <SchemaExistsConfirmation schemaId={this.context.schemaId} error={this.context.error}
                                onConfirm={this.confirmOverride}
                                onCancel={this.denyOverride}/>
      </>
    );
  }
}

export default SaveSchemaStep;
